import { Table, Spinner } from "react-bootstrap";
import TablePaginationBar from "../components/TablePaginationBar";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { FaSearch } from "react-icons/fa";

const SupportRequestsManagement = () => {
  const [allRequests, setAllRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/prompts/getAllUserChatSessionsForAdmin`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const sessionsData = Array.isArray(response.data)
          ? response.data
          : response.data.sessions || [];

        const formattedRequests = sessionsData.map((session) => {
          const requestDate = new Date(session.createdAt);
          const formattedDate = format(requestDate, "MM/dd/yyyy");
          const formattedTime = format(requestDate, "hh:mm a");

          return {
            id: session.id,
            date: session.createdAt,
            formattedDate,
            formattedTime,
            type: session.requestType || "N/A",
            status: session.status || "Active",
            chat: session.chat || [],
          };
        });

        setAllRequests(formattedRequests);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching support requests:", err);
        setError("Failed to load support requests. Please try again later.");
        setLoading(false);
      }
    };

    fetchRequests();
  }, []);

  const filteredRequests = allRequests.filter((request) => {
    const matchesSearch =
      searchTerm === "" ||
      request.id.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.status.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = selectedType === "" || request.type === selectedType;
    const matchesStatus =
      selectedStatus === "" || request.status === selectedStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const uniqueTypes = [...new Set(allRequests.map((request) => request.type))];
  const statusOptions = ["Active", "Inactive"];

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const displayItems = filteredRequests.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleViewRequest = (request) => {
    navigate(`/support-request/${request.id}`, {
      state: { request },
    });
  };

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="app-page">
      <div className="bg-white rounded shadow-sm" style={{ overflow: "hidden" }}>
        <div className="app-page-toolbar">
          <h2 className="app-page-title">Support Requests</h2>

          <div className="app-page-toolbar-filters">
            <div className="app-filter-search-wrap">
              <FaSearch className="app-filter-search-icon" />
              <input
                type="text"
                className="form-control app-filter-search"
                placeholder="Search by ID or Status"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ width: "240px" }}
              />
            </div>

            <select
              className="form-select app-filter-select"
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setCurrentPage(1);
              }}
              style={{ width: "140px" }}
            >
              <option value="">Type</option>
              {uniqueTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <select
              className="form-select app-filter-select"
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setCurrentPage(1);
              }}
              style={{ width: "140px" }}
            >
              <option value="">Status</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-3 text-muted">Loading support requests...</div>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center p-4">No support requests found.</div>
        ) : (
          <>
            <div className="app-page-table-wrap">
              <Table className="admin-table mb-0" hover responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Request Date</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody
                  style={{
                    color: "#575757",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    fontWeight: 500,
                    textAlign: "left",
                  }}
                >
                  {displayItems.map((request) => (
                    <tr key={request.id} style={{ backgroundColor: "white" }}>
                      <td>{String(request.id).padStart(4, "0")}</td>
                      <td>{request.formattedDate}</td>
                      <td>{request.type}</td>
                      <td>{request.status}</td>
                      <td>
                        <button
                          type="button"
                          className="app-view-btn"
                          onClick={() => handleViewRequest(request)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <TablePaginationBar
              currentPage={currentPage}
              totalItems={filteredRequests.length}
              rowsPerPage={itemsPerPage}
              onPageChange={paginate}
              onRowsPerPageChange={(n) => {
                setItemsPerPage(n);
                setCurrentPage(1);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              style={{ paddingLeft: "20px", paddingRight: "20px" }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default SupportRequestsManagement;
